import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { lazyImport } from '../helpers/lazyImport';
import { MainLayout } from '../screens/layouts';
import { TaskLoading } from '../components/commons';
import { useAppSelector } from '../hooks/reduxHooks';
import { isPlatformOwner } from '../helpers/platformAccessHelper';
import { LEVEL2 } from '../constants/constant';
import { RouteGuard } from './RouteGuard';

// Handover feature components
const { HandoverRoutes } = lazyImport(() => import('../features/handover/components'), 'HandoverRoutes');
const { CreateHandover } = lazyImport(() => import('../features/handover/components/CreateHandover'), 'CreateHandover');
const { EditHandover } = lazyImport(() => import('../features/handover/components/EditHandover'), 'EditHandover');
const { AddHandoverTask } = lazyImport(
  () => import('../features/handover/components/AddHandoverTask'),
  'AddHandoverTask'
);

// User feature components
const { Profile } = lazyImport(() => import('../features/user/components/me/Profile'), 'Profile');
const { Users } = lazyImport(() => import('../features/user/components/me/Users'), 'Users');

// Access control components
const { HandoverAccessPermit } = lazyImport(
  () => import('../features/access/components/HandoverAccessPermit'),
  'HandoverAccessPermit'
);
const { HandoverAccessBlock } = lazyImport(
  () => import('../features/access/components/HandoverAccessBlock'),
  'HandoverAccessBlock'
);

// Auth components
const { Register } = lazyImport(() => import('../features/auth/components/register/Register'), 'Register');
const { UpdateRole } = lazyImport(() => import('../features/auth/components/updateRole/UpdateRole'), 'UpdateRole');
const { DeparturePage } = lazyImport(
  () => import('../features/departure/components/DeparturePage'),
  'DeparturePage'
);
const { DepartureDetail } = lazyImport(
  () => import('../features/departure/components/DepartureDetail'),
  'DepartureDetail'
);
const { IncomingViewPage } = lazyImport(
  () => import('../features/incomingView/components/IncomingViewPage'),
  'IncomingViewPage'
);
const { IncomingViewDetailPage } = lazyImport(
  () => import('../features/incomingView/components/IncomingViewDetailPage'),
  'IncomingViewDetailPage'
);
const { ManagerDashboardPage } = lazyImport(
  () => import('../features/managerDashboard/components/ManagerDashboardPage'),
  'ManagerDashboardPage'
);
const { AuditLogPage } = lazyImport(
  () => import('../features/auditLog/components/AuditLogPage'),
  'AuditLogPage'
);
const { BillingPage } = lazyImport(
  () => import('../features/company/components/BillingPage'),
  'BillingPage'
);
const { SupportPage } = lazyImport(
  () => import('../features/company/components/SupportPage'),
  'SupportPage'
);
const { PlatformTrialPage } = lazyImport(
  () => import('../features/company/components/PlatformTrialPage'),
  'PlatformTrialPage'
);
const { MyWorkPage } = lazyImport(
  () => import('../features/myWork/components/MyWorkPage'),
  'MyWorkPage'
);
const { CustomerServiceNotificationsPage } = lazyImport(
  () => import('../features/customerServiceNotifications/components/CustomerServiceNotificationsPage'),
  'CustomerServiceNotificationsPage'
);
const { QuestionnaireTemplatePage } = lazyImport(
  () => import('../features/company/components/QuestionnaireTemplatePage'),
  'QuestionnaireTemplatePage'
);

const AppRoot = () => {
  return (
    <MainLayout>
      <Suspense fallback={<TaskLoading variant="rectangular" count={1} height={200} />}>
        <Outlet />
      </Suspense>
    </MainLayout>
  );
};

const ProtectedHomeRedirect = () => {
  const { userAuth } = useAppSelector((state) => state.auth);
  return (
    <Navigate
      to={isPlatformOwner(userAuth) ? '/internal/trials' : '/my-work'}
      replace
    />
  );
};

export const protectedRoutes = [
  {
    path: '',
    element: <AppRoot />,
    children: [
      // Default redirect
      { path: '/', element: <ProtectedHomeRedirect /> },
      { path: '/auth/*', element: <ProtectedHomeRedirect /> },

      // Home route
      { path: '/my-work', element: <MyWorkPage /> },

      // Handover routes
      { path: '/handovers/*', element: <HandoverRoutes /> },
      { path: '/handovers/create', element: <CreateHandover /> },
      { path: '/edit/:handoverId', element: <EditHandover /> },
      { path: '/task/add/:handoverId', element: <AddHandoverTask /> },

      // Access control routes
      { path: '/access/permit/:handoverId', element: <HandoverAccessPermit /> },
      { path: '/access/block/:handoverId', element: <HandoverAccessBlock /> },

      // User routes
      { path: '/users', element: <Users /> },
      { path: '/profile', element: <Profile /> },

      // Auth routes
      {
        path: '/auth/register',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <Register />
          </RouteGuard>
        ),
      },
      {
        path: '/auth/update/role',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <UpdateRole />
          </RouteGuard>
        ),
      },

      // Departure routes
      { path: '/departure', element: <DeparturePage /> },
      { path: '/departure/:departureId', element: <DepartureDetail /> },

      // Incoming view routes
      { path: '/incoming-view', element: <IncomingViewPage /> },
      { path: '/incoming-view/:viewId', element: <IncomingViewDetailPage /> },

      // Manager dashboard routes
      {
        path: '/dashboard',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <ManagerDashboardPage />
          </RouteGuard>
        ),
      },
      {
        path: '/dashboard/:departureId',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <ManagerDashboardPage />
          </RouteGuard>
        ),
      },
      {
        path: '/audit-log',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <AuditLogPage />
          </RouteGuard>
        ),
      },
      {
        path: '/billing',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <BillingPage />
          </RouteGuard>
        ),
      },
      { path: '/support', element: <SupportPage /> },
      {
        path: '/internal/trials',
        element: (
          <RouteGuard requirePlatformOwner>
            <PlatformTrialPage />
          </RouteGuard>
        ),
      },
      {
        path: '/customer-service-notifications',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <CustomerServiceNotificationsPage />
          </RouteGuard>
        ),
      },
      {
        path: '/departure-template',
        element: (
          <RouteGuard allowedRoles={LEVEL2}>
            <QuestionnaireTemplatePage />
          </RouteGuard>
        ),
      },

      // Catch-all redirect
      { path: '*', element: <Navigate to="/my-work" replace /> },
    ],
  },
];
